import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import rentsService from "../services/rentsService";
import styles from "../assets/rentLicencesEdit.module.css";
import Loader from "../components/Loader";
import pdfIcon from "../assets/images/pdf.png";

const RentLicencesEdit = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [rentData, setRentData] = useState(null);
  const [agreementsHistory, setAgreementsHistory] = useState([]);
  const [formData, setFormData] = useState({
    license_agreement: null,
    signed_license_agreement: null,
  });
  const [uploadingFiles, setUploadingFiles] = useState({
    license_agreement: false,
    signed_license_agreement: false,
  });

  useEffect(() => {
    loadRentData();
  }, [rentId]);

  const loadRentData = async () => {
    try {
      setLoading(true);

      // Fetch real data from API
      const response = await rentsService.getRentLicencesEdit(rentId);

      if (response.success) {
        const { rent, agreementsHistory } = response;

        // Transform rent data for frontend
        const transformedRent = {
          id: rent.id,
          guardian: {
            full_name: rent.guardian_name,
            unique_guardian_number: rent.unique_guardian_number
          },
          license_agreement: rent.license_agreement,
          signed_license_agreement: rent.signed_license_agreement,
          licence_date_signed: rent.licence_date_signed,
          room: rent.room // Include room and property data
        };

        // Log raw backend data first
        console.log('[RENT LICENCES] Raw agreements history from backend:', agreementsHistory);
        
        // Transform agreements history
        const transformedHistory = agreementsHistory.map(agreement => {
          // Parse type_id - handle both string and number formats
          let typeId = null;
          if (agreement.type_id !== undefined && agreement.type_id !== null) {
            typeId = typeof agreement.type_id === 'string' 
              ? parseInt(agreement.type_id, 10) 
              : agreement.type_id;
          }
          
          // Determine type_name from type_id
          let typeName = null;
          if (typeId === 1) {
            typeName = 'license_agreement';
          } else if (typeId === 2) {
            typeName = 'signed_license_agreement';
          }
          
          const transformed = {
            id: agreement.id,
            type_id: typeId, // Ensure it's a number
            type_name: typeName, // Set based on type_id
            path: agreement.path, // Include path for revision links
            created_at: agreement.created_at,
            user: {
              full_name: `${agreement.first_name || ''} ${agreement.last_name || ''}`.trim()
            }
          };
          
          console.log(`[RENT LICENCES] Transformed agreement ${agreement.id}:`, {
            original: {
              type_id: agreement.type_id,
              type_id_type: typeof agreement.type_id
            },
            transformed: {
              type_id: transformed.type_id,
              type_name: transformed.type_name
            }
          });
          
          return transformed;
        });
        
        console.log('[RENT LICENCES] Final transformed agreements history:', transformedHistory);

        setRentData(transformedRent);
        setAgreementsHistory(transformedHistory);
        setFormData({
          license_agreement: null,
          signed_license_agreement: null,
        });
      } else {
        throw new Error(response.message || 'Failed to load data');
      }
    } catch (err) {
      console.error("Error loading rent data:", err);
      toast.error(err.message || "Failed to load licence data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const documentType = name; // 'license_agreement' or 'signed_license_agreement'

      try {
        setUploadingFiles((prev) => ({ ...prev, [name]: true }));
        toast.info(`Uploading ${file.name}...`);

        // Get presigned upload URL
        const uploadData = await rentsService.getLicenseAgreementUploadUrl(
          rentId,
          documentType,
          file.type || 'application/pdf'
        );

        // Upload file directly to S3 using presigned URL
        await rentsService.uploadFileToS3(uploadData.presignedUrl, file);

        // Store filename (file already uploaded to S3) - use functional update to ensure latest state
        setFormData((prev) => {
          const newFormData = {
            ...prev,
            [name]: uploadData.filename, // Store filename, not File object
          };
          
          console.log(`[RENT LICENCES] File uploaded: ${file.name} -> ${uploadData.filename}`);
          console.log(`[RENT LICENCES] Updated formData:`, newFormData);
          
          return newFormData;
        });

        toast.success(`${file.name} uploaded successfully!`);
      } catch (err) {
        console.error(`Error uploading ${name}:`, err);
        toast.error(err.message || `Failed to upload ${file.name}`);
        // Clear the file input on error
        e.target.value = '';
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [name]: false }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasFiles = Object.values(formData).some((filename) => filename !== null);

    if (!hasFiles) {
      toast.error("Please select at least one file to upload");
      return;
    }

    try {
      setSaving(true);

      // Send filenames (files already uploaded to S3 via presigned URLs)
      const submitData = {
        license_agreement: formData.license_agreement || null,
        signed_license_agreement: formData.signed_license_agreement || null,
      };

      // Remove null values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === null) {
          delete submitData[key];
        }
      });

      console.log('[RENT LICENCES] Submitting data:', submitData);
      console.log('[RENT LICENCES] FormData state:', formData);

      if (Object.keys(submitData).length === 0) {
        toast.error("Please select at least one file to upload");
        setSaving(false);
        return;
      }

      const response = await rentsService.updateRentLicences(rentId, submitData);

      if (response.success) {
        toast.success(response.message || "Licences updated successfully!");
        loadRentData(); // Reload to show updated data
        // Clear form
        setFormData({
          license_agreement: null,
          signed_license_agreement: null,
        });
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      console.error("Error updating licences:", err);
      toast.error(err.message || "Failed to update licences");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (documentType) => {
    if (window.confirm("Are you sure you want to remove this document?")) {
      try {
        setDeletingDocument(documentType);
        const response = await rentsService.deleteLicenceDocument(rentId, documentType);

        if (response.success) {
          toast.success(response.message || "Document removed successfully!");
          loadRentData(); // Reload to show updated data
        } else {
          throw new Error(response.message || 'Delete failed');
        }
      } catch (err) {
        console.error("Error deleting document:", err);
        toast.error(err.message || "Failed to delete document");
      } finally {
        setDeletingDocument(null);
      }
    }
  };

  const handleGenerateLicence = async () => {
    try {
      setGenerating(true);
      await rentsService.generateLicenceAgreement(rentId, 'license_agreement');
      toast.success("Licence agreement generated and downloaded successfully!");
      loadRentData(); // Reload to show updated data
    } catch (err) {
      console.error("Error generating licence:", err);
      toast.error(err.message || "Failed to generate licence agreement");
    } finally {
      setGenerating(false);
    }
  };

  const handleDocumentView = async (rentId, documentType, revisionId = null) => {
    try {
      let url;
      if (revisionId) {
        url = await rentsService.getLicenceDocumentFromRevision(rentId, documentType, revisionId);
      } else {
        url = await rentsService.getLicenceDocument(rentId, documentType);
      }

      // Open presigned URL in new tab
      window.open(url, '_blank');
      toast.success("Document opened in new tab");
    } catch (err) {
      console.error("Error viewing document:", err);
      
      // Match Laravel's error message for missing files
      if (err.response?.status === 404 || err.response?.data?.message?.includes('not found')) {
        toast.error("The file doesn't exist on server. Please report that to the admin.");
      } else {
        const errorMessage = err.response?.data?.message || err.message || "Failed to open document";
        toast.error(errorMessage);
      }
      
      // If file doesn't exist, show more details in console
      if (err.response?.data?.expectedPath) {
        console.error('Expected file path:', err.response.data.expectedPath);
        console.error('File may need to be re-uploaded');
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!rentData) {
    return (
      <div className={styles["rent-licences-edit"]}>
        <div className={styles["error-container"]}>
          Error: Unable to load rent data
        </div>
      </div>
    );
  }

  return (
    <div className={styles["rent-licences-edit"]}>
      <div className={styles["container"]}>
        {/* Breadcrumbs - Laravel exact match */}
        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>
            <ol className={styles["breadcrumb"]}>
              <li className={styles["active"]}>Dashboard</li>
            </ol>
          </div>
        </div>

        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>

            <form onSubmit={handleSubmit} className={styles["mt-30"]}>

              {/* Guardian and Property Information */}
              <div className={`${styles["panel-title"]} ${styles["title-standard"]} ${styles["col-black"]}`}>
                <div>Guardian: <strong>{rentData.guardian?.full_name} ({rentData.guardian?.unique_guardian_number})</strong></div>
                <h3>Property: </h3> {rentData.room?.property?.full_address}
                <div>Property date take on: <strong>{rentData.room?.property?.date_taken_on_show}</strong></div>
                <div>Property date take back: <strong>{rentData.room?.property?.date_taken_back_show}</strong></div>
              </div>

              <div className={`${styles["panel-group"]} ${styles["border-down"]} ${styles["margin-down"]}`}>

                <div className={styles["row"]}>

                  <div className={styles["col-xs-6"]}>
                    {/* License Agreement */}
                    {rentData.license_agreement ? (
                      <div className={styles["property-application-license-agreement"]}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDocumentView(rentId, 'license_agreement');
                          }}
                        >
                          <div className={styles["btn-property"]}>
                            <div className={styles["document-left"]}>
                              <div className={styles["pdf-icon"]}><img src={pdfIcon} alt="PDF" /></div>
                            </div>
                            <div className={styles["document-right"]}>
                              <p className={styles["btn-title"]}>Agreement</p>
                              <p className={styles["btn-short"]}>Click to open & print</p>
                            </div>
                          </div>
                        </a>

                        <div className={styles["clearfix"]}></div>

                        <div className={styles["document-actions"]}>
                          <button
                            type="button"
                            className={`${styles["block"]} ${styles["document-action"]} ${styles["document-action-delete"]} ${styles["width-100"]} ${styles["hidden-print"]}`}
                            onClick={() => handleDeleteDocument('license_agreement')}
                            disabled={deletingDocument === 'license_agreement'}
                          >
                            {deletingDocument === 'license_agreement' ? 'Removing...' : 'Remove'}
                          </button>
                        </div>

                      </div>
                    ) : (
                      <div className={styles["property-application-signed-license-agreement"]}>
                        <div className={styles["col-xs-12"]}>
                          <div className={styles["input-image"]}>
                            <input
                              type="file"
                              name="license_agreement"
                              className={styles["form-control"]}
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileChange}
                              disabled={uploadingFiles.license_agreement}
                            />
                            {uploadingFiles.license_agreement && (
                              <span style={{ marginLeft: '10px', color: '#007bff' }}>Uploading...</span>
                            )}
                          </div>
                        </div>
                        <div className={styles["clearfix"]}></div>
                      </div>
                    )}
                  </div>

                  <div className={styles["col-xs-6"]}>
                    {/* Signed License Agreement */}
                    {rentData.signed_license_agreement ? (
                      <div className={styles["property-application-license-agreement"]}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDocumentView(rentId, 'signed_license_agreement');
                          }}
                        >
                          <div className={styles["btn-property"]}>
                            <div className={styles["document-left"]}>
                              <div className={styles["pdf-icon"]}><img src={pdfIcon} alt="PDF" /></div>
                            </div>
                            <div className={styles["document-right"]}>
                              <p className={styles["btn-title"]}>Signed Agreement</p>
                              <p className={styles["btn-short"]}>Click to open & print</p>
                            </div>
                          </div>
                        </a>

                        <div className={styles["clearfix"]}></div>

                        <div className={styles["document-actions"]}>
                          <button
                            type="button"
                            className={`${styles["block"]} ${styles["document-action"]} ${styles["document-action-delete"]} ${styles["width-100"]} ${styles["hidden-print"]}`}
                            onClick={() => handleDeleteDocument('signed_license_agreement')}
                            disabled={deletingDocument === 'signed_license_agreement'}
                          >
                            {deletingDocument === 'signed_license_agreement' ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles["property-application-signed-license-agreement"]}>
                        <div className={styles["col-xs-12"]}>
                          <div className={styles["input-image"]}>
                            <input
                              type="file"
                              name="signed_license_agreement"
                              className={styles["form-control"]}
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileChange}
                              disabled={uploadingFiles.signed_license_agreement}
                            />
                            {uploadingFiles.signed_license_agreement && (
                              <span style={{ marginLeft: '10px', color: '#007bff' }}>Uploading...</span>
                            )}
                          </div>
                        </div>
                        <div className={styles["clearfix"]}></div>
                      </div>
                    )}
                  </div>
                  <div className={styles["clearfix"]}></div>

                </div>

                <div className={`${styles["form-group"]} ${styles["text-right"]} ${styles["mt-30"]}`}>
                  <button
                    type="submit"
                    className={`${styles["btn"]} ${styles["btn-head"]} ${styles["btn-standard"]}`}
                    disabled={saving}
                  >
                    {saving ? "Updating..." : "Update Licences"}
                  </button>
                </div>

              </div>

            </form>

          </div>

          {/* Generate Licences Section */}
          <div className={styles["col-xs-12"]}>

            <div className={`${styles["panel-title"]} ${styles["title-standard"]}`}>
              <h3>Generate Licences</h3>
            </div>

            <div className={`${styles["panel-group"]} ${styles["border-down"]} ${styles["margin-down"]}`}>

              <div className={styles["row"]}>

                <div className={styles["col-xs-3"]}>
                  <button
                    type="button"
                    className={`${styles["block"]} ${styles["btn-generate-licence"]} ${styles["hidden-print"]}`}
                    onClick={handleGenerateLicence}
                    disabled={generating}
                  >
                    {generating ? "Generating..." : "Generate Licence Agreement"}
                  </button>
                </div>
              </div>

              <div className={styles["clearfix"]}></div>
            </div>

          </div>

          {/* License Agreements Revision History */}
          {agreementsHistory && agreementsHistory.length > 0 && (
            <div className={styles["col-xs-12"]}>
              <div className={`${styles["panel-title"]} ${styles["title-standard"]}`}>
                <h3>License agreements revision</h3>
              </div>

              <div className={`${styles["panel-group"]} ${styles["border-down"]} ${styles["margin-down"]}`}>

                <table className={styles["table"]}>
                  <thead>
                    <tr>
                      <th>Link</th>
                      <th>License</th>
                      <th>Deleted date</th>
                      <th>Deleted by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreementsHistory.map((agreement) => {
                      // Determine license name - match Laravel's logic exactly
                      let licenseName = "Signed license agreement"; // Default fallback
                      
                      // Get type_id - already parsed in transformation, but double-check
                      const typeId = agreement.type_id !== undefined && agreement.type_id !== null 
                        ? (typeof agreement.type_id === 'string' ? parseInt(agreement.type_id, 10) : agreement.type_id)
                        : null;
                      
                      console.log(`[RENT LICENCES] Rendering agreement row:`, {
                        id: agreement.id,
                        type_id: agreement.type_id,
                        type_id_parsed: typeId,
                        type_name: agreement.type_name,
                        path: agreement.path
                      });
                      
                      // Determine license name based on type_id (primary) or type_name (fallback)
                      if (typeId === 1) {
                        licenseName = "License agreement";
                      } else if (typeId === 2) {
                        licenseName = "Signed license agreement";
                      } else if (agreement.type_name === 'license_agreement') {
                        licenseName = "License agreement";
                      } else if (agreement.type_name === 'signed_license_agreement') {
                        licenseName = "Signed license agreement";
                      } else {
                        // If we still don't have a match, log warning but show default
                        console.warn('[RENT LICENCES] Could not determine license name for agreement:', {
                          id: agreement.id,
                          type_id: agreement.type_id,
                          type_name: agreement.type_name,
                          using_default: licenseName
                        });
                      }
                      
                      // Final check - ensure we always have a value
                      if (!licenseName || licenseName.trim() === '') {
                        licenseName = "Signed license agreement"; // Ultimate fallback
                      }
                      
                      return (
                        <tr key={agreement.id}>
                          <td>
                            <a
                              href="#"
                              className={styles["col-black"]}
                              onClick={(e) => {
                                e.preventDefault();
                                // Determine document type from type_id (1 = license_agreement, 2 = signed_license_agreement)
                                // This matches Laravel's agreement_type mapping
                                const documentType = (typeId === 1) 
                                  ? 'license_agreement' 
                                  : (typeId === 2) 
                                    ? 'signed_license_agreement' 
                                    : agreement.type_name || 'license_agreement'; // Fallback
                                // Pass revision ID (agreement.id) to view the history version, not current version
                                console.log(`[RENT LICENCES] Viewing revision:`, {
                                  rentId,
                                  documentType,
                                  revisionId: agreement.id,
                                  filename: agreement.path,
                                  typeId
                                });
                                handleDocumentView(rentId, documentType, agreement.id);
                              }}
                            >
                              <img src={pdfIcon} alt="PDF" />LINK
                            </a>
                          </td>
                          <td style={{ padding: '8px', minHeight: '20px' }}>
                            {/* Always render license name - ensure it's visible */}
                            {licenseName || `Unknown (type_id: ${agreement.type_id || 'null'})`}
                          </td>
                        <td>{new Date(agreement.created_at).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}</td>
                        <td>
                          {agreement.user ? agreement.user.full_name : ''}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>

              </div>

            </div>
          )}


        </div>

      </div>

    </div>
  );
};

export default RentLicencesEdit;